import { useDrag } from "react-dnd";
import classNames from "classnames";

import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR } from "../Alerts/Alert";

export const MAP_ELEMENT_DRAGGABLE_SOURCE = "MapElementDraggableSource";
export const SOURCE_TYPE_NEW_CONTRIBUTION = "NEW_CONTRIBUTION";

const MapElementDraggableSource = props => {
  const { onDragStart, onDragEnd, onDropped, dropErrorMessage, sourceItem, children } =
    props;

  const { showAlert } = useAlert();

  // This serves us as a drag start hook and to provide the dragger marker style to the
  // drop zone so it can calculate the exact location the marker was dropped to by taking
  // the marker display offset into account.
  //
  const item = () => {
    if (onDragStart) onDragStart();

    return sourceItem;
  };

  // This is the drag end callback that is called whenever the draggable element is
  // dropped inside a valid drop container.
  //
  // The drop container (a `<MapDroppableZone>` component droppable instance) takes care
  // of ensuring that the dropped element is inside the target zone and returns an
  // `ìsValid` property, along with the coordinates (x/y and lat/lng) where the element
  // was dropped.
  const end = (_, monitor) => {
    const didDrop = monitor.didDrop();
    const dropResult = didDrop && monitor.getDropResult();

    if (onDragEnd) onDragEnd();

    if (didDrop && dropResult.isValid) {
      onDropped && onDropped(dropResult);
    } else if (dropErrorMessage) {
      showAlert({ message: dropErrorMessage, type: ALERT_TYPE_ERROR });
    }
  };

  // This function is run for each native drag event while the element is being dragged
  // and returns the props that we want to retrieve inside the component body. Here we
  // only need to know the current drag state of the element to adapt how we display it.
  //
  // The returned props are then accessible from the first element returned by the
  // `useDrag` hook call below.
  const collect = monitor => ({ isDragging: monitor.isDragging() });

  // Connect the component to the react-dnd's drag and drop context and retrieve the
  // monitored props. The `dragRef` returned variable is then attached to our container
  // so react-dnd can hande drag events and drag preview display.
  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
    () => ({
      type: MAP_ELEMENT_DRAGGABLE_SOURCE,
      item,
      end,
      collect
    }),
    [onDragStart, onDragEnd, onDropped, dropErrorMessage, sourceItem]
  );

  const className = classNames([
    "map-element-draggable-source",
    { dragging: isDragging }
  ]);

  // The `willChange` style property is used for IE to handle properly the react-dnd's
  // drag preview mechanism.
  return (
    <div {...{ className }} style={{ willChange: "transform" }} ref={dragRef}>
      {children(dragPreviewRef)}
    </div>
  );
};

export default MapElementDraggableSource;
